import { useRef, useState } from 'react';
import { Phone } from 'lucide-react';
import type { BookingResponse, ObjectApartmentSummary } from '@/shared/api';
import { BottomSheet } from '@/shared/components';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { BookingActionDialogs } from './BookingActionDialogs';
import { BookingActionList, type BookingActionKind } from './BookingActionList';
import { bookingActionFlags } from './bookingActionRules';
import { useOtaEarlyDeparture } from './useOtaEarlyDeparture';

interface Props {
  booking: BookingResponse;
  guestName: string;
  /** Nur mit Gastdaten-Recht; dann steht „Anrufen“ in der Leiste unten. */
  guestPhone: string | null;
  propertyName: string | null;
  manageable: boolean;
  properties: ObjectApartmentSummary[];
}

/**
 * Aktionen der Buchung: ab 1280 px eine klebende rechte Spalte, darunter eine Leiste unten mit
 * „Anrufen“ und „Aktionen“, die dieselbe Liste als Blatt öffnet (Board „BuchungHandy“).
 */
export function BookingActionWorkspace({
  booking,
  guestName,
  guestPhone,
  propertyName,
  manageable,
  properties,
}: Props) {
  const [open, setOpen] = useState<BookingActionKind | null>(null);
  const [sheet, setSheet] = useState(false);
  const wide = useMediaQuery('(min-width: 1280px)');
  const flags = bookingActionFlags(booking, manageable, properties.length);
  const earlyDeparture = useOtaEarlyDeparture(booking, open === 'early', () => setOpen(null));
  // Aus dem Blatt öffnet der Dialog erst, wenn das Blatt den Fokus an „Aktionen“ zurückgegeben
  // hat: so merkt sich der Dialog „Aktionen“ als Rücksprung, statt eines Eintrags im Blatt, den
  // es nach dem Schließen nicht mehr gibt.
  const pending = useRef<BookingActionKind | null>(null);
  // Wird das Fenster breit, schließt Radix das Blatt ohne `onOpenChange`: der Zustand folgt nach,
  // sonst hielte eine Aktion aus der Spalte das Blatt noch für offen und täte nichts.
  if (wide && sheet) setSheet(false);
  if (!manageable || (!flags.any && !guestPhone)) return null;
  const sheetOpen = sheet && !wide;
  const pick = (kind: BookingActionKind) => {
    if (sheetOpen) {
      pending.current = kind;
      setSheet(false);
    } else {
      setOpen(kind);
    }
  };
  const list = (
    <BookingActionList
      booking={booking}
      flags={flags}
      onPick={pick}
      onCancelPlan={earlyDeparture.cancelPlan}
      planPending={earlyDeparture.pending}
    />
  );
  return (
    <>
      {wide ? (
        flags.any && (
          <aside
            aria-labelledby="booking-actions"
            className="border-hairline rounded-tile sticky top-4 flex h-fit flex-col gap-3 border bg-white p-5"
          >
            <h2
              id="booking-actions"
              className="text-ink-soft font-mono text-[10px] font-semibold uppercase tracking-[0.16em]"
            >
              Aktionen
            </h2>
            {list}
          </aside>
        )
      ) : (
        <div
          role="group"
          aria-label="Buchungsaktionen"
          className="border-hairline shadow-panel fixed bottom-[calc(var(--shell-inset,0px)+var(--shell-bottom-bar,0px))] left-[calc(var(--shell-inset,0px)+var(--shell-nav-width,0px))] right-[var(--shell-inset,0px)] z-30 flex gap-2 border-t bg-white p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
        >
          {guestPhone && (
            <a
              href={`tel:${guestPhone.replace(/\s/g, '')}`}
              className="rounded-pill border-ink flex h-12 flex-1 items-center justify-center gap-2 border-[1.5px] text-sm font-bold"
            >
              <Phone size={16} aria-hidden /> Anrufen
            </a>
          )}
          {flags.any && (
            <button
              type="button"
              onClick={() => setSheet(true)}
              className="rounded-pill bg-ink flex h-12 flex-[2] items-center justify-center text-sm font-bold text-white"
            >
              Aktionen
            </button>
          )}
        </div>
      )}
      <BottomSheet
        open={sheetOpen}
        onOpenChange={setSheet}
        title="Aktionen"
        description={`Buchung ${booking.bookingReference}`}
        onClosed={() => {
          if (pending.current) setOpen(pending.current);
          pending.current = null;
        }}
      >
        {list}
      </BottomSheet>
      <BookingActionDialogs
        booking={booking}
        flags={flags}
        open={open}
        onClose={() => setOpen(null)}
        guestName={guestName}
        propertyName={propertyName}
        properties={properties}
        earlyDeparture={earlyDeparture}
      />
    </>
  );
}
